import { useQuery } from '@tanstack/react-query'
import { Button, StateBlock } from '@w7/ui'
import { useState } from 'react'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'

function snapNum(snap: Record<string, unknown>, key: string): string {
  const v = snap[key]
  if (v == null) return '—'
  const n = Number(v)
  return Number.isFinite(n) ? String(n) : String(v)
}

export { snapNum }

export function MaxLimitLogsPage() {
  const [page, setPage] = useState(1)
  const pageSize = 50

  const logsQ = useQuery({
    queryKey: ['cm', 'event-type-max-limit-logs', page, pageSize],
    queryFn: () => cmApi.listEventTypeMaxLimitLogs(authHeaders(), { page, pageSize }),
  })

  const logs = logsQ.data?.logs ?? []
  const total = logsQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  if (logsQ.isLoading && !logs.length) {
    return (
      <StateBlock icon="activity" title="Loading Max Limit logs…">
        Reading Lab EventTypeMaxLimitLog via CM-API.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="Max Limit Logs"
      subtitle="Lab EventTypeMaxLimitLog — who changed sport caps, from which IP"
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={logsQ.isFetching}
          onClick={() => void logsQ.refetch()}
        >
          Refresh
        </Button>
      }
    >
      {logsQ.isError ? (
        <div className="cm-hint" role="alert">
          <p>{(logsQ.error as Error).message}</p>
          <Button
            type="button"
            variant="secondary"
            busy={logsQ.isFetching}
            onClick={() => void logsQ.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      {!logs.length && !logsQ.isLoading && !logsQ.isError ? (
        <p className="cm-hint">No Max Limit changes logged yet. Save Max Limits to create entries.</p>
      ) : (
        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>When</th>
                <th>Sport</th>
                <th>Action</th>
                <th>By</th>
                <th>IP</th>
                <th>Min</th>
                <th>Max Bet</th>
                <th>Max Market</th>
                <th>BM Max</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((row, i) => (
                <tr key={row.id}>
                  <td>{(page - 1) * pageSize + i + 1}</td>
                  <td>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleString()
                      : '—'}
                  </td>
                  <td>{row.sportName || row.sportId || '—'}</td>
                  <td>{row.action}</td>
                  <td className="w7-truncate" title={row.modifiedByName ?? undefined}>
                    {row.modifiedByName ?? '—'}
                  </td>
                  <td>{row.ip ?? '—'}</td>
                  <td>{snapNum(row.snapshot, 'minBet')}</td>
                  <td>{snapNum(row.snapshot, 'maxBetAmt')}</td>
                  <td>{snapNum(row.snapshot, 'maxMarketAmount')}</td>
                  <td>{snapNum(row.snapshot, 'maxBetBookmaker')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize ? (
        <div className="cm-actions">
          <Button
            type="button"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="cm-hint">
            Page {page} / {totalPages} · {total} total
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </CmPanel>
  )
}
